import { ApiService } from '../../../services/api.service';
import { AuthService } from '../../../services/auth.service';
import { CommonModule } from '@angular/common';
import { Component, ElementRef, HostListener, OnInit } from '@angular/core';
import { RouterModule } from '@angular/router';
import { NavBarComponent } from '../../../shared/components/nav-bar/nav-bar.component';
import { FormsModule } from '@angular/forms';
import { StorageService } from '../../../services/storage.service';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

@Component({
  selector: 'app-leaderboard',
  imports: [CommonModule, RouterModule, NavBarComponent, FormsModule],
  templateUrl: './leaderboard.component.html',
  styleUrl: './leaderboard.component.css'
})
export class LeaderboardComponent implements OnInit {
  tabs = ['Today', 'Weekly', 'Monthly', 'All'];
  activeProfitTab: 'profit' | 'volume' = 'profit';
  activeTab = 'Today';
  isOpen = false;
  selectedCategory = 'All Categories';
  selectedCategoryId: string | null = null;
  categories: any[] = [];

  // Leaderboard logic
  leaderboardData: any[] = [];
  topWinsData: any[] = [];
  userStats: any = null;
  loading = false;
  topWinsLoading = false;
  isLoadingMore = false;
  searchQuery = '';
  page = 1;
  pageSize = 20;
  hasMoreData = true;
  private searchSubject = new Subject<string>();

  constructor(
    private elementRef: ElementRef,
    private api: ApiService,
    private auth: AuthService,
    private storage: StorageService
  ) {
    this.searchSubject.pipe(
      debounceTime(500),
      distinctUntilChanged()
    ).subscribe(query => {
      this.searchQuery = query;
      this.fetchLeaderboard();
    });
  }

  ngOnInit() {
    this.loadCategories();
    this.fetchLeaderboard();
    this.fetchTopWins();
  }

  loadCategories() {
    const data = this.storage.getCategoryData();
    if (data && data.length > 0) {
      this.categories = [{ id: null, name: 'All Categories' }, ...data];
    } else {
      // Fallback if data is not yet loaded in storage
      this.categories = [{ id: null, name: 'All Categories' }];
      this.storage.categoryUpdate$.subscribe(updated => {
        if (updated) {
          this.categories = [{ id: null, name: 'All Categories' }, ...this.storage.getCategoryData()];
        }
      });
    }
  }

  fetchLeaderboard(isLoadMore: boolean = false) {
    // Reset pagination on fresh load
    if (!isLoadMore) {
      this.page = 1;
      this.leaderboardData = [];
      this.hasMoreData = true;
      this.loading = true;
      this.isLoadingMore = false;
    } else {
      if (this.isLoadingMore || !this.hasMoreData) {
        return;
      }
      this.isLoadingMore = true;
    }

    const params: any = {
      page: this.page,
      page_size: this.pageSize,
      time_filter: this.activeTab.toUpperCase(), // TODAY, WEEKLY, MONTHLY, ALL
      sort_by: this.activeProfitTab === 'profit' ? 'PNL' : 'VOLUME', // PNL, VOLUME
      sort_direction: 'DESC'
    };

    if (this.searchQuery) {
      params.search = this.searchQuery;
    }

    if (this.selectedCategoryId) {
      params.category_id = this.selectedCategoryId;
    }

    const userData = this.auth.getUserData();
    if (userData && (userData.id || userData.user_id)) {
      params.user_id = userData.id || userData.user_id;
    }

    this.api.getLeaderboard(params).subscribe({
      next: (res) => {
        const newData = (res.data.results || []).map((item: any) => ({
          ...item,
          user_id: item.user_id,
          profile_picture: item.profile_picture,
          created_at: item.created_at
        }));
        // console.log('fetchLeaderboard results mapped sample:', newData[0]);

        if (isLoadMore) {
          this.leaderboardData = [...this.leaderboardData, ...newData];
        } else {
          this.leaderboardData = newData;
        }

        this.userStats = res.data.user_stats ? {
          ...res.data.user_stats,
          user_id: res.data.user_stats.user_id || res.data.user_stats.id || res.data.user_stats.user_uuid,
          profile_picture: res.data.user_stats.profile_picture || res.data.user_stats.profile_pic
        } : null;

        // Check if there are more records
        this.hasMoreData = newData.length === this.pageSize;
        this.page++;

        this.loading = false;
        this.isLoadingMore = false;
      },
      error: (err) => {
        console.error('Error fetching leaderboard:', err);
        this.loading = false;
        this.isLoadingMore = false;

        if (!isLoadMore) {
          this.leaderboardData = []; // Fallback empty
        }
      }
    });
  }

  fetchTopWins() {
    this.topWinsLoading = true;

    this.api.getLeaderboardTopWins().subscribe({
      next: (res) => {
        this.topWinsData = (res?.data || []).map((item: any, index: number) => ({
          rank: index + 1,
          user_id: item.user_id,
          username: item.username,
          profile_picture: item.profile_pic,
          market_name: item.market_name,
          invested_amount: this.parseAmount(item.invested_amnt),
          winning_amount: this.parseAmount(item.winning_amount),
          profit: this.parseAmount(item.profit)
        }));
        this.topWinsLoading = false;
      },
      error: (err) => {
        console.error('Error fetching top wins:', err);
        this.topWinsData = [];
        this.topWinsLoading = false;
      }
    });
  }

  parseAmount(amount: any): number {
    if (!amount) return 0;
    if (typeof amount === 'number') return amount;
    return parseFloat(amount.toString().replace(/[^0-9.-]+/g, '')) || 0;
  }

  formatCurrency(value: any, formatOrShowPlus?: string | boolean): string {
    if (value === null || value === undefined) return '$0.00';
    const numValue = typeof value === 'number' ? value : parseFloat(value.toString().replace(/[^0-9.-]+/g, ''));
    if (isNaN(numValue)) return '$0.00';

    const showPlus = typeof formatOrShowPlus === 'boolean' ? formatOrShowPlus : false;
    const isNoDecimal = typeof formatOrShowPlus === 'string' && formatOrShowPlus.endsWith('-0');

    const formatted = Math.abs(numValue).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: isNoDecimal ? 0 : 2,
      maximumFractionDigits: isNoDecimal ? 0 : 2
    });

    if (numValue < 0) return '-' + formatted;
    return (showPlus ? '+' : '') + formatted;
  }

  onSearchChange() {
    this.searchSubject.next(this.searchQuery);
  }

  setProfitTab(tab: 'profit' | 'volume') {
    this.activeProfitTab = tab;
    this.fetchLeaderboard();
  }

  setActiveTab(tab: string) {
    this.activeTab = tab;
    this.fetchLeaderboard();
  }

  selectCategory(category: any) {
    this.selectedCategory = category.name;
    this.selectedCategoryId = category.id;
    this.isOpen = false;
    this.fetchLeaderboard();
  }

  toggleDropdown() {
    this.isOpen = !this.isOpen;
  }

  @HostListener('document:click', ['$event'])
  onOutsideClick(event: Event) {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.isOpen = false;
    }
  }

  shortenUsername(username: string): string {
    if (!username) return 'Anonymous';
    return username.length > 15 ? username.slice(0, 15) + '...' : username;
  }

  @HostListener('window:scroll', [])
  onWindowScroll() {
    const pos = (document.documentElement.scrollTop || document.body.scrollTop) + document.documentElement.offsetHeight;
    const max = document.documentElement.scrollHeight;
    if (pos >= max - 200) {
      if (this.hasMoreData && !this.isLoadingMore && !this.loading) {
        this.fetchLeaderboard(true);
      }
    }
  }
}
